/**
 * Mundalo - branded toast notifications.
 *
 * Single canonical entry point for all user-facing alerts/errors/successes.
 * Built on top of the existing `sonner` library (mounted in App.js).
 *
 * Use:
 *   import { notifyError, notifySuccess, notifyInfo } from '@/lib/notify';
 *
 *   notifyError(apiResponseBody, 'Could not save.');  // accepts API payloads
 *   notifyError('Plain message');                     // or a string
 *   notifySuccess('Saved!');
 *   notifyInfo('Heads up');
 *
 * `notifyError(payload, fallback)` runs `extractApiError` so we never render
 * "[object Object]" or a raw HTTPException dict.
 */
import { toast } from 'sonner';
import { extractApiError } from './apiError';

const baseStyle = {
  fontFamily: 'inherit',
  letterSpacing: '0.01em',
  borderRadius: '12px',
};

const errorStyle = {
  ...baseStyle,
  background: '#0A1F2E',
  color: '#F9F6EF',
  border: '1px solid #B91C1C',
};

const successStyle = {
  ...baseStyle,
  background: '#0A1F2E',
  color: '#F9F6EF',
  border: '1px solid #2CA8A0',
};

const infoStyle = {
  ...baseStyle,
  background: '#F9F6EF',
  color: '#0A1F2E',
  border: '1px solid #E0D6B8',
};

export function notifyError(payload, fallback = 'Something went wrong - please try again.') {
  const message = typeof payload === 'string' ? payload : extractApiError(payload, fallback);
  return toast.error(message, { style: errorStyle, duration: 6000 });
}

export function notifySuccess(message) {
  return toast.success(message, { style: successStyle, duration: 4000 });
}

export function notifyInfo(message) {
  return toast(message, { style: infoStyle, duration: 4000 });
}
